import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UserBlockAnswers } from '../../../../../../config/api/models/Answers';
import { CircularProgress, Divider, useTheme } from '@material-ui/core';
import StudentAnswerAttempt from './StudentAnswerAttempt';
import StudentQuestionSetAttempt from './StudentQuestionSetAttempt';
import { Exercise, ExerciseType } from '../../../../../../config/api/models/Exercise';
import { Solution, SolutionSubContent } from '../../../../../../config/api/models/Solutions';
import { StudentSolution } from '../solution';
import { AnswersLabel, AnswersName, AttemptsWrapper, BackToGroupButton, NoAnswerMessage } from './Atoms';
import { FontAwesomeSpriteIcon } from 'src/modules/shared/components/FontAwesomeLocal';
import { SpriteIconsLight } from 'src/config/api/models/FontAwesome';
import { useAppDispatch, useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import { setSelectedStudent } from 'src/modules/shared/slices/filters/entity/FiltersSlice';
import { useGetStudentByGroupQuery } from 'src/modules/shared/slices/student/api/students';
import { Message } from 'src/modules/shared/components';
import { useUser } from 'src/modules/shared';

type Props = {
  exercise: Exercise;
  data?: UserBlockAnswers;
  solution?: Solution;
};

const StudentAnswers: FC<Props> = ({ exercise, data, solution }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const selectedGroup = useAppSelector((state) => state.filters.selectedGroup);
  const selectedStudent = useAppSelector((state) => state.filters.selectedStudent);
  const { isTeacher } = useUser();
  
  const {
    data: studentsData,
    isError: studentsIsError,
    isSuccess: studentsIsSuccess,
    isLoading: studentsIsLoading,
  } = useGetStudentByGroupQuery(selectedGroup || '', {skip: !isTeacher});

  const selectedStudentData = useMemo(() => {
    if (studentsData === undefined) return undefined;
    if (selectedStudent === undefined) return undefined;

    return studentsData?.find((student) => student.id === selectedStudent);
  }, [studentsData, selectedStudent]);

  const handleBackToGroupButton = useCallback(() => {
    dispatch(setSelectedStudent(''));
  }, [dispatch]);

  if (studentsIsLoading) {
    return <CircularProgress />;
  }

  if (studentsIsError) {
    return <Message type="error">{t('trajectory.sections.shared.answers.error')}</Message>;
  }

  if (studentsIsSuccess && studentsData?.length === 0) {
    return <Message type="info">{t('trajectory.sections.shared.answers.noResults')}</Message>;
  }

  return (
    <>
      {studentsIsSuccess && !!studentsData && !!selectedStudentData && (
        <>
          <BackToGroupButton onClick={handleBackToGroupButton}>
            <FontAwesomeSpriteIcon
              icon={SpriteIconsLight.BACK_BUTTON}
              color={theme.palette.text.primary}
            />
            {t('trajectory.sections.shared.answers.backToGroup')}{' '}
          </BackToGroupButton>
          <AnswersLabel>
            {t('trajectory.sections.shared.answers.answersOf')}{' '}
            <AnswersName>{selectedStudentData.fullName}</AnswersName>
            {':'}
          </AnswersLabel>
        </>
      )}

      {!isTeacher && selectedGroup === '' && !!selectedStudent && (
        <>
          <AnswersLabel>
            {t('trajectory.sections.shared.answers.myAnswers')}
            {' (' +
              (data?.numAttempts ?? 0) +
              ' ' +
              t('trajectory.sections.shared.answers.attempts') +
              ')'}
          </AnswersLabel>
        </>
      )}

      <AttemptsWrapper>
        {!data && <NoAnswerMessage>Nog geen antwoorden</NoAnswerMessage>}

        {/*H5P Question Set exercises*/}
        {!!data?.questionSetId && data?.questionSetAnswers && Object.entries(data?.questionSetAnswers).map(([label, value], index) => (
          <React.Fragment key={index}>
            {index !== 0 && <Divider />}
            <StudentQuestionSetAttempt
              questionType={exercise.question_type}
              label={label}
              attempt={value}
              index={index}
              solutionAnswers={solution?.answers as SolutionSubContent[]}
            />
          </React.Fragment>
        ))}

        {/*H5P/BookWidgets normal exercises*/}
        {data?.attempts != null && !data?.questionSetId && exercise.type !== ExerciseType.ALGEBRA_KIT && Object.entries(data?.attempts).map(([label, value], index) => (
          <React.Fragment key={index}>
            {index !== 0 && <Divider />}
            <StudentAnswerAttempt
              questionType={exercise.question_type}
              label={label}
              attempt={value}
              index={index}
            />
          </React.Fragment>
        ))}

        {/*Algebra Kit exercises*/}
        {exercise.type === ExerciseType.ALGEBRA_KIT && data?.questionSetAnswers && Object.entries(data?.questionSetAnswers).map(([label, value], index) => (
          <React.Fragment key={index}>
            {index !== 0 && <Divider />}
            <StudentQuestionSetAttempt
              questionType={exercise.question_type}
              label={label}
              attempt={value}
              index={index}
              solutionAnswers={solution?.answers as SolutionSubContent[]}
            />
          </React.Fragment>
        ))}               

        {solution != null && (
          <>
            <Divider />
            <StudentSolution solution={solution} />
          </>
        )}
      </AttemptsWrapper>
    </>
  );
};

export default StudentAnswers;
